import {
  App,
  areFENummerEqual,
  compareProductIdsIfExistingAndSpLaufnrNotExists,
  FEVorvertragsnummer,
  FremdVN,
  FremdVNVorvertrag,
  VnWechselAngaben,
  VnWechselAngabenElement,
  VnWechselVertraegeAendern,
} from '@ps20-frontend/frontend-models';
import {
  GlobalActions,
  NavigationRoute,
  ProduktDeleted,
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { AnyAction } from 'redux';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { Produkt } from '../model';
import { CheckoutActions } from '../checkout.actions';

function produktDeleted(sectionState: VnWechselAngaben, action: AnyAction): VnWechselAngaben {
  const produktDeletedPayload: ProduktDeleted = action.payload;

  if (
    sectionState.angabenListe &&
    sectionState.angabenListe.length > 0 &&
    produktDeletedPayload.vorvertragsnummern &&
    produktDeletedPayload.vorvertragsnummern.length > 0
  ) {
    const newState: VnWechselAngaben = cloneDeep(sectionState);
    const deletedAppIds = new Set<App>(
      produktDeletedPayload.angebotsIds.map(produkt => produkt.appId),
    );
    newState.angabenListe.forEach(angabe => {
      angabe.fremdVNVorvertraege.forEach(fremdvnVorvertrag => {
        produktDeletedPayload.vorvertragsnummern.forEach(vorvertragsnummern => {
          if (
            areFENummerEqual(vorvertragsnummern, fremdvnVorvertrag.vorvertragsnummer) &&
            fremdvnVorvertrag.vnWechselGrund
          ) {
            fremdvnVorvertrag.vnWechselGrund = fremdvnVorvertrag.vnWechselGrund.filter(
              grund => !deletedAppIds.has(grund.appId),
            );
          }
        });
      });
    });
    return newState;
  }
  return sectionState;
}

export const vnWechselAngabenReducer = function (
  sectionState: VnWechselAngaben = null,
  action: AnyAction,
  state: IAppState,
): VnWechselAngaben {
  switch (action.type) {
    case GlobalActions.NAVIGATION_COMPLETED: {
      const actionPayload: NavigationRoute = action.payload;

      if (actionPayload.appId === App.SHOP_CHECKOUT) {
        const newState: VnWechselAngaben = cloneDeep(sectionState);
        newState.vnWechselGrundAngabenVorhanden = false;
        const produkte: Array<Produkt> = state.produkte.filter(
          produkt => produkt.vorvertragsnummer && produkt.vorvertragsnummer.length > 0,
        );

        const fremdVertraege: Array<FremdVNVorvertrag> = newState.angabenListe
          .reduce(
            (accumulator, angabenElement) => accumulator.concat(angabenElement.fremdVNVorvertraege),
            [],
          )
          // Fix für Bugs: Doppelte Anzeige von VN-Wechselgründen im Checkout (PS20-79633)
          // und Vertreterwechsel mit VN-Wechsel PS1.0 Verträge (PS20-80904)
          .filter(
            (value, index, vertraege) =>
              index ===
              vertraege.findIndex(vertrag =>
                isVorvertragIdentical(vertrag.vorvertragsnummer, value.vorvertragsnummer),
              ),
          );

        fremdVertraege.forEach(fremdVertrag => {
          const einschliessendeProdukte: Array<Produkt> = produkte.filter(produkt =>
            produkt.vorvertragsnummer.some(
              vorvertragnummer =>
                fremdVertrag.vorvertragsnummer.vertragsnummer === vorvertragnummer.vertragsnummer &&
                compareProductIdsIfExistingAndSpLaufnrNotExists(
                  fremdVertrag.vorvertragsnummer.produktIds,
                  vorvertragnummer.produktIds,
                  fremdVertrag.vorvertragsnummer.spLaufnummer,
                  vorvertragnummer.spLaufnummer,
                ),
            ),
          );

          if (einschliessendeProdukte && einschliessendeProdukte.length > 0) {
            if (!fremdVertrag.vnWechselGrund || fremdVertrag.vnWechselGrund.length === 0) {
              fremdVertrag.vnWechselGrund = [];
              einschliessendeProdukte.forEach(produkt =>
                fremdVertrag.vnWechselGrund.push({ appId: produkt.appId }),
              );
            }
            newState.vnWechselGrundAngabenVorhanden = true;
          }
        });
        return newState;
      }
      return sectionState;
    }
    case GlobalActions.PRODUKT_DELETED:
      return produktDeleted(sectionState, action);
    case GlobalActions.VN_WECHSEL_CHANGED: {
      const payload: VnWechselVertraegeAendern = action.payload;
      const fremdVn = getPersonForVnWechsel(payload, state, action);
      const addVertraege: Array<FremdVNVorvertrag> = action.payload.addVertraege;
      const removeVertraege: Array<FremdVNVorvertrag> = action.payload.removeVertraege;

      if (removeVertraege && removeVertraege.length > 0) {
        sectionState.angabenListe.forEach(
          angabe =>
            (angabe.fremdVNVorvertraege = angabe.fremdVNVorvertraege.filter(
              vertrag =>
                !removeVertraege.some(removedVertrag =>
                  areFENummerEqual(removedVertrag.vorvertragsnummer, vertrag.vorvertragsnummer),
                ),
            )),
        );
      }

      if (fremdVn) {
        const existingVNElement: VnWechselAngabenElement = sectionState.angabenListe.find(angabe =>
          isEqual(fremdVn, angabe.fremdVn),
        );

        if (addVertraege && addVertraege.length > 0) {
          if (existingVNElement?.fremdVNVorvertraege) {
            existingVNElement.fremdVNVorvertraege =
              payload.isWeitererVertrag === true
                ? addVertraege
                : existingVNElement.fremdVNVorvertraege.concat(addVertraege);
          } else {
            const newVnWechselAngabenElement: VnWechselAngabenElement = {
              fremdVn: fremdVn,
              fremdVNVorvertraege: addVertraege,
            };
            sectionState.angabenListe.push(newVnWechselAngabenElement);
          }
        }
      }

      sectionState.angabenListe = sectionState.angabenListe.filter(
        angabe => angabe.fremdVNVorvertraege && angabe.fremdVNVorvertraege.length > 0,
      );

      return sectionState;
    }
    case GlobalActions.VN_WECHSEL_GRUND_CHANGED: {
      const payload: FremdVNVorvertrag = action.payload;

      const found: FremdVNVorvertrag = sectionState.angabenListe
        .reduce((acc, angabe) => acc.concat(angabe.fremdVNVorvertraege), [])
        .find(fremdVNVorvertrag =>
          areFENummerEqual(fremdVNVorvertrag.vorvertragsnummer, payload.vorvertragsnummer),
        );
      if (found) {
        found.vnWechselGrund = payload.vnWechselGrund;
        found.wasSelectedInVwg = payload.wasSelectedInVwg;
      }

      return sectionState;
    }
    case CheckoutActions.VN_WECHSELGRUND_LIST_RECEIVED: {
      return { ...sectionState, vnWechselgrundList: action.payload };
    }
    default:
      return sectionState;
  }
};

export function isVorvertragIdentical(
  vertrag1: FEVorvertragsnummer,
  vertrag2: FEVorvertragsnummer,
): boolean {
  const checkWithProduktIds = Boolean(
    vertrag1 &&
      vertrag2 &&
      vertrag1.vertragsnummer === vertrag2.vertragsnummer &&
      vertrag1.spLaufnummer === vertrag2.spLaufnummer &&
      vertrag1.produktIds &&
      vertrag2.produktIds &&
      vertrag1.produktIds.length === vertrag2.produktIds.length &&
      vertrag1.produktIds.every((produktId, index) => produktId === vertrag2.produktIds[index]),
  );
  const checkWithoutProduktIds = Boolean(
    vertrag1 &&
      vertrag2 &&
      vertrag1.vertragsnummer === vertrag2.vertragsnummer &&
      vertrag1.spLaufnummer === vertrag2.spLaufnummer,
  );
  return ![null, undefined].includes(vertrag1?.produktIds) &&
    ![null, undefined].includes(vertrag2?.produktIds)
    ? checkWithProduktIds
    : checkWithoutProduktIds;
}

function getPersonForVnWechsel(
  payload: VnWechselVertraegeAendern,
  state: IAppState,
  action: AnyAction,
): FremdVN {
  if (payload.isWeitererVertrag) {
    return {
      vorname: state.versicherungsnehmer.vorname,
      nachname: state.versicherungsnehmer.nachname,
      akadgrad: state.versicherungsnehmer.akadgrad,
      adelsbez: state.versicherungsnehmer.adelsbez,
      anrede: state.versicherungsnehmer.anrede,
      adresse: {
        strasse: state.versicherungsnehmer.strasse,
        plz: state.versicherungsnehmer.plz,
        hausnummer: state.versicherungsnehmer.hausnummer,
        ort: state.versicherungsnehmer.ort,
        land: state.versicherungsnehmer.land,
      },
    };
  } else {
    return action.payload.fremdVn;
  }
}
